import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getCampaigns, createCampaign, updateCampaign, deleteCampaign, getClients } from '@/lib/api';
import { toast } from 'sonner';

const CHANNELS = [
  { value: 'meta', label: 'Meta (Facebook/Instagram)' },
  { value: 'google', label: 'Google App Campaigns' },
  { value: 'tiktok', label: 'TikTok Ads' },
  { value: 'apple_search_ads', label: 'Apple Search Ads' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Aktiv' },
  { value: 'paused', label: 'Pausiert' },
  { value: 'completed', label: 'Abgeschlossen' },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterChannel, setFilterChannel] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({ client_id: '', name: '', channel: 'meta', status: 'active', budget: 0, target_cpi: 0, notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campRes, clRes] = await Promise.all([getCampaigns(), getClients()]);
      setCampaigns(campRes.data);
      setClients(clRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editingCampaign) {
        await updateCampaign(editingCampaign.id, form);
        toast.success('Kampagne aktualisiert');
      } else {
        await createCampaign(form);
        toast.success('Kampagne erstellt');
      }
      setDialogOpen(false);
      setEditingCampaign(null);
      loadData();
    } catch (err) { toast.error('Fehler beim Speichern'); }
  };

  const handleEdit = (camp) => {
    setEditingCampaign(camp);
    setForm({ client_id: camp.client_id, name: camp.name, channel: camp.channel, status: camp.status, budget: camp.budget, target_cpi: camp.target_cpi, notes: camp.notes || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kampagne wirklich löschen?')) return;
    try { await deleteCampaign(id); toast.success('Kampagne gelöscht'); loadData(); } catch (err) { toast.error('Fehler'); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unbekannt';

  const filtered = campaigns.filter(c => {
    const matchSearch = c.name?.toLowerCase().includes(search.toLowerCase());
    const matchChannel = filterChannel === 'all' || c.channel === filterChannel;
    return matchSearch && matchChannel;
  });

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Kampagnen" description={`${campaigns.length} Kampagnen verwaltet`}>
        <Button onClick={() => { setEditingCampaign(null); setForm({ client_id: clients[0]?.id || '', name: '', channel: 'meta', status: 'active', budget: 0, target_cpi: 0, notes: '' }); setDialogOpen(true); }} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="campaign-create-button">
          <Plus className="w-4 h-4 mr-2" /> Neue Kampagne
        </Button>
      </PageHeader>

      <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Kampagnen suchen..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={filterChannel} onValueChange={setFilterChannel}>
          <SelectTrigger className="w-full sm:w-52"><SelectValue placeholder="Kanal" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kanäle</SelectItem>
            {CHANNELS.map(ch => <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </motion.div>

      {filtered.length === 0 && !loading ? (
        <EmptyState title="Keine Kampagnen" description="Erstellen Sie Ihre erste Kampagne." actionLabel="Kampagne erstellen" action={() => setDialogOpen(true)} />
      ) : (
        <motion.div variants={fadeIn} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaigns-table">
              <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left p-3 pl-4 font-medium text-slate-500">Name</th>
                <th className="text-left p-3 font-medium text-slate-500">Kunde</th>
                <th className="text-left p-3 font-medium text-slate-500">Kanal</th>
                <th className="text-right p-3 font-medium text-slate-500">Budget (€)</th>
                <th className="text-right p-3 font-medium text-slate-500">Ziel-CPI (€)</th>
                <th className="text-left p-3 font-medium text-slate-500">Status</th>
                <th className="text-right p-3 pr-4 font-medium text-slate-500">Aktionen</th>
              </tr></thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id || i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50" data-testid="campaigns-table-row">
                    <td className="p-3 pl-4 font-medium text-slate-900">{c.name}</td>
                    <td className="p-3 text-slate-700">{getClientName(c.client_id)}</td>
                    <td className="p-3"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">{CHANNELS.find(ch => ch.value === c.channel)?.label || c.channel}</span></td>
                    <td className="p-3 text-right tabular-nums text-slate-700">{c.budget?.toLocaleString('de-DE')}</td>
                    <td className="p-3 text-right tabular-nums text-slate-700">{c.target_cpi?.toFixed(2)}</td>
                    <td className="p-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700' : c.status === 'paused' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{STATUS_OPTIONS.find(s => s.value === c.status)?.label || c.status}</span></td>
                    <td className="p-3 pr-4 text-right"><div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editingCampaign ? 'Kampagne bearbeiten' : 'Neue Kampagne'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Kampagnenname" data-testid="campaign-form-name" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Kunde</label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}><SelectTrigger data-testid="campaign-form-client"><SelectValue placeholder="Kunde wählen" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Kanal</label>
                <Select value={form.channel} onValueChange={v => setForm({...form, channel: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CHANNELS.map(ch => <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Budget (€)</label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: parseFloat(e.target.value) || 0})} /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Ziel-CPI (€)</label><Input type="number" step="0.01" value={form.target_cpi} onChange={e => setForm({...form, target_cpi: parseFloat(e.target.value) || 0})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="campaign-form-save">{editingCampaign ? 'Aktualisieren' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
